/**
 * Reports Service
 * CRUD operations for report_schedules table
 */

import { supabase } from "./supabase";
import type { ReportSchedule } from "../types/event.types";

export async function getEventReportSchedules(
  eventId: string,
): Promise<ReportSchedule[]> {
  const { data, error } = await supabase
    .from("report_schedules")
    .select("*")
    .eq("event_id", eventId)
    .eq("report_type", "event_redemption")
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }
  return data || [];
}

export async function getPromoterReportSchedule(
  orgId: string,
): Promise<ReportSchedule | null> {
  const { data, error } = await supabase
    .from("report_schedules")
    .select("*")
    .eq("organization_id", orgId)
    .eq("report_type", "promoter_summary")
    .maybeSingle();

  if (error) {
    throw error;
  }
  return data;
}

export async function createReportSchedule(input: {
  report_type: "event_redemption" | "promoter_summary";
  event_id?: string;
  organization_id?: string;
  recipient_emails: string[];
  scheduled_at?: string;
  is_recurring?: boolean;
  date_from?: string;
  date_to?: string;
  attachment_format?: "csv" | "xlsx";
}): Promise<ReportSchedule> {
  const { data, error } = await supabase
    .from("report_schedules")
    .insert({
      ...input,
      is_recurring: input.is_recurring ?? false,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
}

/**
 * Sentinel error message thrown when an update touches a schedule that is
 * no longer editable (already sent or deactivated). Surface this to the
 * UI as a stale-state toast + refresh rather than a generic error.
 */
export const SCHEDULE_NO_LONGER_EDITABLE = "SCHEDULE_NO_LONGER_EDITABLE";

/** Update payload for updateReportSchedule.
 *
 *  date_from and date_to use `string | null` (not undefined-only) so the
 *  caller can EXPLICITLY clear them. The helper preserves explicit null
 *  in the PostgREST payload by checking `'key' in input` — omitting the
 *  key leaves the column untouched (PATCH semantics), passing null
 *  clears it. Audit-AI 2026-05-28 R2-SR2. */
export interface UpdateReportScheduleInput {
  recipient_emails?: string[];
  scheduled_at?: string;
  is_active?: boolean;
  is_recurring?: boolean;
  date_from?: string | null;
  date_to?: string | null;
  attachment_format?: "csv" | "xlsx";
}

export async function updateReportSchedule(
  id: string,
  updates: UpdateReportScheduleInput,
): Promise<ReportSchedule> {
  // Build the patch by checking key presence rather than truthiness so
  // an explicit null on date_from / date_to survives to the .update()
  // payload. Without this, `updates.date_from === null` would be sent
  // by Supabase-js as a literal null (good), BUT if the caller passed
  // `undefined` it would also be sent — which Supabase-js would coerce
  // to setting the column to null inadvertently. The `in` check
  // distinguishes "operator explicitly cleared" from "field absent."
  const patch: Record<string, unknown> = {};
  if ("recipient_emails" in updates) {
    patch.recipient_emails = updates.recipient_emails;
  }
  if ("scheduled_at" in updates) {
    patch.scheduled_at = updates.scheduled_at;
  }
  if ("is_active" in updates) {
    patch.is_active = updates.is_active;
  }
  if ("is_recurring" in updates) {
    patch.is_recurring = updates.is_recurring;
  }
  if ("date_from" in updates) {
    patch.date_from = updates.date_from;
  }
  if ("date_to" in updates) {
    patch.date_to = updates.date_to;
  }
  if ("attachment_format" in updates) {
    patch.attachment_format = updates.attachment_format;
  }

  // R9 (2026-05-25): server-side guard against editing a schedule that
  // has already been sent or is inactive. UI gating alone is racy when
  // the cron worker runs concurrently. PostgREST returns PGRST116
  // "JSON object requested, multiple (or no) rows returned" when
  // .single() finds no matching row — that's our signal that the guard
  // predicates filtered it out (already sent or inactive).
  const { data, error } = await supabase
    .from("report_schedules")
    .update(patch)
    .eq("id", id)
    .is("last_sent_at", null)
    .eq("is_active", true)
    .select()
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      throw new Error(SCHEDULE_NO_LONGER_EDITABLE);
    }
    throw error;
  }
  return data;
}

export async function deleteReportSchedule(id: string): Promise<void> {
  const { error } = await supabase
    .from("report_schedules")
    .delete()
    .eq("id", id);

  if (error) {
    throw error;
  }
}
